import type { ServiceResponse } from './types';
import { apiRequest } from '../utils/api';
import { API_ENDPOINTS } from '../config/api.config';

// Backend response type
interface FlashNewsResponseDto {
  _id: string;
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'error';
  link?: string;
  linkText?: string;
  isActive: boolean;
  priority: number;
  startDate?: string;
  endDate?: string;
  createdAt: string;
  updatedAt: string;
  dismissedBy?: string[];
}

// Frontend type
export interface FlashNews {
  id: string;
  title?: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'alert';
  link?: {
    text: string;
    url: string;
  };
  active: boolean;
  priority: number;
  createdAt: string;
  expiresAt?: string;
}

class FlashNewsService {
  private readonly DISMISSED_KEY = 'dismissed_flash_news';

  // Transform backend flash news to frontend format
  private transformFlashNews(news: FlashNewsResponseDto): FlashNews {
    return {
      id: news._id || news.id,
      title: news.title,
      message: news.message,
      type: news.type === 'error' ? 'alert' : news.type,
      link: news.link && news.linkText ? {
        text: news.linkText,
        url: news.link
      } : undefined,
      active: news.isActive,
      priority: news.priority,
      createdAt: news.createdAt,
      expiresAt: news.endDate
    };
  }

  // Get dismissed news IDs from localStorage
  private getDismissedIds(): string[] {
    try {
      const dismissed = localStorage.getItem(this.DISMISSED_KEY);
      return dismissed ? JSON.parse(dismissed) : [];
    } catch {
      return [];
    }
  }

  // Save dismissed news ID
  private saveDismissedId(id: string): void {
    try {
      const dismissed = this.getDismissedIds();
      if (!dismissed.includes(id)) {
        dismissed.push(id);
        localStorage.setItem(this.DISMISSED_KEY, JSON.stringify(dismissed));
      }
    } catch (error) {
      console.error('Error saving dismissed flash news:', error);
    }
  }

  // Get all active flash news
  async getActiveFlashNews(): Promise<ServiceResponse<FlashNews[]>> {
    try {
      const response = await apiRequest<FlashNewsResponseDto[]>(
        API_ENDPOINTS.FLASH_NEWS.ACTIVE
      );

      const dismissedIds = this.getDismissedIds();
      const now = new Date();

      // Filter and transform flash news
      const activeNews = response
        .filter(news => {
          // Filter out dismissed news
          if (dismissedIds.includes(news._id || news.id)) {
            return false;
          }
          
          // Check if expired
          if (news.endDate && new Date(news.endDate) < now) {
            return false;
          }
          
          // Check if not yet started
          if (news.startDate && new Date(news.startDate) > now) {
            return false;
          }
          
          return news.isActive;
        })
        .map(news => this.transformFlashNews(news))
        .sort((a, b) => a.priority - b.priority);

      return {
        success: true,
        data: activeNews
      };
    } catch (error) {
      console.error('Error fetching flash news:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Failed to fetch flash news'
      };
    }
  }

  // Get all flash news (including inactive)
  async getAllFlashNews(): Promise<ServiceResponse<FlashNews[]>> {
    try {
      const response = await apiRequest<FlashNewsResponseDto[]>(
        API_ENDPOINTS.FLASH_NEWS.BASE
      );

      const transformedNews = response
        .map(news => this.transformFlashNews(news))
        .sort((a, b) => a.priority - b.priority);

      return {
        success: true,
        data: transformedNews
      };
    } catch (error) {
      console.error('Error fetching all flash news:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Failed to fetch flash news'
      };
    }
  }

  // Dismiss flash news
  async dismissFlashNews(id: string): Promise<ServiceResponse<void>> {
    try {
      // Save to local storage
      this.saveDismissedId(id);

      // Try to notify backend (optional, don't fail if it errors)
      try {
        const user = JSON.parse(localStorage.getItem('auth_user') || '{}');
        if (user.id) {
          await apiRequest<void>(
            API_ENDPOINTS.FLASH_NEWS.DISMISS(id),
            {
              method: 'POST',
            },
            true
          );
        }
      } catch {
        // Ignore backend errors for dismissal
      }

      return {
        success: true,
        data: undefined
      };
    } catch (error) {
      console.error('Error dismissing flash news:', error);
      return {
        success: false,
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to dismiss flash news'
      };
    }
  }

  // Clear all dismissed news (for testing)
  clearDismissedNews(): void {
    localStorage.removeItem(this.DISMISSED_KEY);
  }

  // Check if a specific news item is dismissed
  isNewsDismissed(id: string): boolean {
    const dismissedIds = this.getDismissedIds();
    return dismissedIds.includes(id);
  }
}

export const flashNewsService = new FlashNewsService();